import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from 'lucide-react';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { UnderwritingSlideOut } from '@/components/UnderwritingSlideOut';
import { isKnownLimitationError, isUnexpectedError } from '@/util/quoteTypeGuards';

import { FormProvider, useForm } from 'react-hook-form';
import { useDebounce } from '@/hooks/useDebounce';
import { useErrorToasts } from '@/hooks/useErrorToasts';
import { useProductDiscovery } from '@/hooks/useProductDiscovery';
import { useSelector } from 'react-redux';

import {
  selectClient,
  selectClientCalculationSettings,
  selectMaybeClientPartner,
  selectQuotes,
  selectQuotesError,
  selectQuotesIsLoading,
  selectAllPaymentOptionQuotes,
  selectIsLoadingAllPaymentOptions,
} from '@/model';
import {
  selectFeatureFlags,
  selectSessionAdvisor,
  selectActiveFundingSourceOperation,
} from '@/model/selectors';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import {
  AdjustedQuotesBanner,
  DEFAULT_FILTERS_STATE,
  QuoteErrorDisplay,
  ShopInsuranceAcknowledgmentOverlay,
  ShopInsuranceContext,
  ShopInsuranceFiltersSidebar,
  ShopInsuranceHeader,
  ShopInsuranceNotice,
  ShopInsuranceTabContent,
  ShopInsuranceTabsWrapper,
  mapMaritalStatusToTraditionalLTC,
  getHideSinglePayAnd10Pay,
  getShowSinglePayAnd10Pay,
  getShowTraditionalLTCOptions,
  useAdjustedQuotes,
  useAllPaymentOptionQuotesFetching,
  useBenefitDesignInputs,
  useBenefitDesignQuotes,
  useEligibilityFilters,
  useFilteredTraditionalLTCQuotes,
  useQuoteFetching,
  useQuoteSelection,
  useShopInsuranceAcknowledgment,
  useShopInsuranceState,
  useSliderBounds,
  useSliderBoundsSync,
  useTraditionalLTCQuoteFetching,
  useUnderwritingDetailsView,
  useUnderwritingResults,
  useUnderwritingSearch,
  type CombinedFilterType,
  type ShopInsuranceModalPropsOptions,
} from '@/features/shop-insurance';
import { Button } from '@design-system';

import { zipCodeToState } from '@shared';

interface FormData {
  premium: number;
  benefitDuration: CombinedFilterType;
  inflationProtection: CombinedFilterType;
}

export function ShopInsuranceContent({
  onClose,
  queryOptions,
}: {
  onClose: () => void;
  queryOptions?: ShopInsuranceModalPropsOptions;
}) {
  const navigate = useNavigate();
  const { flags } = useFeatureFlags();
  const skipAutoFetch = queryOptions?.skipAutoFetch ?? false;
  useErrorToasts({ open: true });

  const quotes = useSelector(selectQuotes);
  const allPaymentOptionQuotes = useSelector(selectAllPaymentOptionQuotes);
  const isLoadingAllPaymentOptions = useSelector(selectIsLoadingAllPaymentOptions);
  const client = useSelector(selectClient);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const state = zipCodeToState(client.intakeSurvey.clientZipCode);

  const {
    selectedEligibilityFilters,
    setSelectedEligibilityFilters,
    selectedFeatureBenefitsFilters,
    setSelectedFeatureBenefitsFilters,
    handleRemoveEligibilityFilter,
    handleRemoveFeatureBenefitsFilter,
  } = useEligibilityFilters();

  const error = useSelector(selectQuotesError);
  const isLoading = useSelector(selectQuotesIsLoading);
  const featureFlags = useSelector(selectFeatureFlags);

  const {
    selectedTab,
    paymentOption,
    mode,
    expandedIndices,
    hasAcknowledged,
    showAcknowledgment,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    hasSameGenderPartner,
    handleTabChange,
    handleCardToggle,
    handleAcknowledgment,
    setShowAcknowledgment,
    handleBuyerOptionChange,
    handlePaymentOptionChange,
    handleModeChange,
  } = useShopInsuranceState({
    hideSinglePayAnd10Pay: getHideSinglePayAnd10Pay(
      featureFlags.hideSinglePayAnd10Pay,
      client.intakeSurvey.clientBirthDate,
    ),
    providedBuyerOption: queryOptions?.buyerOption,
    providedPaymentOption: queryOptions?.paymentOption,
    providedSelectedTab: queryOptions?.selectTabOption,
  });

  const { fetchQuotesForPolicyType } = useQuoteFetching({
    open: true,
    selectedTab,
    paymentOption,
    hasPartnerWithDemographics,
    skipAutoFetch,
  });

  const sliderBounds = useSliderBounds(quotes, selectedTab);
  const { isQuoteSelected } = useQuoteSelection({ selectedTab });

  const returnOnInvestmentCalculationType =
    clientCalculationSettings?.returnOnInvestmentCalculationType || 'total';

  const formMethods = useForm<FormData>({
    defaultValues: {
      ...DEFAULT_FILTERS_STATE,
      premium: sliderBounds.defaultValue,
    },
  });

  const { watch, reset, getValues } = formMethods;

  const premiumValue = useDebounce(watch('premium'), 200);
  const benefitDurationValue = useDebounce(watch('benefitDuration'), 200);
  const inflationProtectionValue = useDebounce(watch('inflationProtection'), 200);

  useSliderBoundsSync({ sliderBounds, paymentOption, reset, getValues });

  useTraditionalLTCQuoteFetching({
    open: true,
    paymentOption,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    advisorId,
  });

  const { filteredTraditionalLTCQuotes } = useFilteredTraditionalLTCQuotes({
    paymentOption,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    returnOnInvestmentCalculationType,
  });

  useShopInsuranceAcknowledgment({
    open: true,
    isLoading,
    quotes,
    hasAcknowledged,
    setShowAcknowledgment,
  });

  function handleTabChangeWithFetch(tabValue: string) {
    handleTabChange(tabValue);
    fetchQuotesForPolicyType(tabValue as ShopInsuranceTab, paymentOption);
  }

  const {
    inputs: benefitDesignInputs,
    setMonthlyBenefit,
    setBenefitDuration,
    setInflationProtection,
  } = useBenefitDesignInputs();

  useAllPaymentOptionQuotesFetching({
    mode,
    selectedTab,
    hasPartnerWithDemographics,
  });

  const { adjustedQuotes, isAdjusted } = useAdjustedQuotes({
    client,
    selectedTab,
    returnOnInvestmentCalculationType,

    originalQuotes: quotes,
    selectedPremium: premiumValue,
    selectedBenefitDuration: benefitDurationValue,
    selectedInflationProtection: inflationProtectionValue,
    selectedFeatureBenefitsFilters,
  });

  const { quoteGroups: benefitDesignQuoteGroups, availableOptions: benefitDesignAvailableOptions } =
    useBenefitDesignQuotes({
      originalQuotes: allPaymentOptionQuotes.length ? allPaymentOptionQuotes : quotes,
      client,
      selectedTab,
      inputs: benefitDesignInputs,
      returnOnInvestmentCalculationType,
    });

  const benefitDesignFlatQuotes = benefitDesignQuoteGroups.map(group => group.representativeQuote);

  const modeAdjustedQuotes = mode === 'benefit-design' ? benefitDesignFlatQuotes : adjustedQuotes;
  const modeTraditionalLTCQuotes = mode === 'benefit-design' ? [] : filteredTraditionalLTCQuotes;

  const { productsToSearch } = useProductDiscovery({
    adjustedQuotes: modeAdjustedQuotes,
    traditionalLTCQuotes: modeTraditionalLTCQuotes,
    isJoint: selectedTab === 'joint',
  });

  const {
    fetchStarted: underwritingFetchStarted,
    fetchComplete: underwritingFetchComplete,
    results: searchResults,
  } = useUnderwritingSearch({
    clientId: client?.clientId,
    productsToSearch,
    isJoint: selectedTab === 'joint',
    shouldStart:
      !!client?.clientId && (modeAdjustedQuotes.length > 0 || modeTraditionalLTCQuotes.length > 0),
  });

  const {
    underwritingResults,
    eligibilityCounts,
    filteredAdjustedQuotes,
    filteredTraditionalLTCQuotesWithEligibility,
    showEligibilityFilter,
  } = useUnderwritingResults({
    adjustedQuotes: modeAdjustedQuotes,
    filteredTraditionalLTCQuotes: modeTraditionalLTCQuotes,
    searchResults,
    selectedTab,
    underwritingFetchComplete,
    selectedEligibilityFilters,
  });

  const {
    showUnderwritingSlideOut,
    setShowUnderwritingSlideOut,
    selectedUnderwritingData,
    handleViewUnderwritingDetails,
  } = useUnderwritingDetailsView({ searchResults, selectedTab });

  function handleOnClose() {
    localStorage.removeItem('benefitDurationNotice');
    localStorage.removeItem('inflationProtectionNotice');
    onClose();
  }

  const activeFundingSourceOperation = useSelector(selectActiveFundingSourceOperation);
  const hasSelectedPolicies =
    Object.keys(client?.multipleFundingSources?.ltcPolicy ?? {}).length > 0;
  const hasQuotesLoaded = (quotes?.length ?? 0) > 0;
  const isReloading = activeFundingSourceOperation !== null;
  const isInitialLoading = isLoading && !hasQuotesLoaded;
  const hasUnexpectedError = isUnexpectedError(error);
  const knownLimitationError = isKnownLimitationError(error) ? error : null;
  const showContentArea = !isInitialLoading && !hasUnexpectedError;

  const tabContent = useMemo(
    () => (
      <ShopInsuranceTabContent
        underwritingFetchStarted={underwritingFetchStarted}
        underwritingFetchComplete={underwritingFetchComplete}
        filteredAdjustedQuotes={filteredAdjustedQuotes}
        filteredTraditionalLTCQuotesWithEligibility={filteredTraditionalLTCQuotesWithEligibility}
        underwritingResults={underwritingResults}
        expandedIndices={expandedIndices}
        selectedTab={selectedTab}
        clientPartner={clientPartner}
        premiumValue={premiumValue}
        paymentOption={paymentOption}
        advisorId={advisorId}
        isQuoteSelected={isQuoteSelected}
        handleCardToggle={handleCardToggle}
        handleViewUnderwritingDetails={handleViewUnderwritingDetails}
        onClose={handleOnClose}
        knownLimitationError={knownLimitationError}
        hasSameGenderPartner={hasSameGenderPartner}
        mode={mode}
        benefitDesignQuoteGroups={benefitDesignQuoteGroups}
        stateOfResidence={state.stateAbbreviation}
      />
    ),
    [
      underwritingFetchStarted,
      underwritingFetchComplete,
      filteredAdjustedQuotes,
      filteredTraditionalLTCQuotesWithEligibility,
      underwritingResults,
      expandedIndices,
      selectedTab,
      clientPartner,
      premiumValue,
      paymentOption,
      advisorId,
      isQuoteSelected,
      handleCardToggle,
      handleViewUnderwritingDetails,
      handleOnClose,
      knownLimitationError,
      hasSameGenderPartner,
      mode,
      benefitDesignQuoteGroups,
      state.stateAbbreviation,
    ],
  );

  const shopInsuranceFiltersProps = {
    selectedEligibilityFilters,
    setSelectedEligibilityFilters,
    showEligibilityFilter: flags.nycLifeDemo ? false : showEligibilityFilter,
    eligibilityCounts,
    selectedFeatureBenefitsFilters,
    setSelectedFeatureBenefitsFilters,
    mode,
    handleModeChange,
    benefitDesignInputs,
    benefitDesignAvailableOptions,
    setMonthlyBenefit,
    setBenefitDuration,
    setInflationProtection,
  };

  const isMarriedOrPartnered =
    mapMaritalStatusToTraditionalLTC(client?.intakeSurvey?.clientMaritalStatus) === 'Married' ||
    hasPartnerWithDemographics;

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      data-testid="shop-insurance-content"
    >
      <ShopInsuranceContext.Provider
        value={{
          sliderBounds,
          paymentOption,
          handlePaymentOptionChange,
          showTraditionalLTCOptions: getShowTraditionalLTCOptions(
            client.intakeSurvey.clientZipCode,
            client.intakeSurvey.clientBirthDate,
          ),
          showSinglePayAnd10Pay: getShowSinglePayAnd10Pay(
            featureFlags.hideSinglePayAnd10Pay,
            client.intakeSurvey.clientBirthDate,
          ),
        }}
      >
        <FormProvider {...formMethods}>
          <ShopInsuranceAcknowledgmentOverlay
            open={showAcknowledgment && !hasAcknowledged}
            onAcknowledge={handleAcknowledgment}
          />

          {/* HEADER — sticky above scroll */}
          <div className="border-border bg-background shrink-0 border-b px-4">
            <div className="mx-auto w-full max-w-7xl">
              <ShopInsuranceHeader
                isLoading={isLoading}
                error={error}
                onClose={handleOnClose}
              />
            </div>
          </div>

          {/* BODY — scrollable area */}
          <div className="relative min-h-0 flex-1 overflow-y-auto px-4 pt-6 lg:pt-0">
            {isReloading && (
              <div className="bg-background/50 fixed inset-0 z-10 grid h-screen w-screen place-items-center">
                <Loader className="text-primary size-8 animate-spin" />
              </div>
            )}
            <div className="mx-auto w-full max-w-7xl">
              {isInitialLoading && (
                <div
                  className="flex min-h-[60vh] items-center justify-center"
                  data-testid="shop-insurance-loading"
                >
                  <LoadingSpinner testId="shop-insurance-loading-spinner" />
                </div>
              )}
              {hasUnexpectedError && (
                <div className="py-10">
                  <QuoteErrorDisplay
                    error={error}
                    onRetry={() => fetchQuotesForPolicyType(selectedTab, paymentOption)}
                  />
                </div>
              )}

              {showContentArea && (
                <div className="flex flex-col lg:flex-row lg:gap-x-8">
                  {/* SIDEBAR — fixed width, own scroll if content overflows */}
                  <ShopInsuranceFiltersSidebar {...shopInsuranceFiltersProps} />

                  {/* RIGHT COLUMN */}
                  <div className="flex flex-1 flex-col">
                    {mode === 'premium-optimization' &&
                      (isAdjusted.length > 0 ||
                        (!flags.nycLifeDemo &&
                          showEligibilityFilter &&
                          selectedEligibilityFilters.length > 0) ||
                        selectedFeatureBenefitsFilters.length > 0) && (
                        <div className="bg-background sticky top-0 z-50 -mx-1 shrink-0 px-1 pb-2 pt-6">
                          <AdjustedQuotesBanner
                            premiumValue={premiumValue}
                            benefitDurationValue={benefitDurationValue}
                            inflationProtectionValue={inflationProtectionValue}
                            appliedFilters={isAdjusted}
                            selectedEligibilityFilters={
                              flags.nycLifeDemo || !showEligibilityFilter
                                ? []
                                : selectedEligibilityFilters
                            }
                            onRemoveEligibilityFilter={handleRemoveEligibilityFilter}
                            selectedFeatureBenefitsFilters={selectedFeatureBenefitsFilters}
                            onRemoveFeatureBenefitsFilter={handleRemoveFeatureBenefitsFilter}
                          />
                        </div>
                      )}

                    <div className="pt-6">
                      <ShopInsuranceTabsWrapper
                        hasPartnerWithDemographics={hasPartnerWithDemographics}
                        isMarriedOrPartnered={isMarriedOrPartnered}
                        paymentOption={paymentOption}
                        selectedTab={selectedTab}
                        selectedBuyerOption={selectedBuyerOption}
                        tabContent={tabContent}
                        handleTabChangeWithFetch={handleTabChangeWithFetch}
                        handleBuyerOptionChange={handleBuyerOptionChange}
                        featureFlags={featureFlags}
                        mode={mode}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {showContentArea && (
              <div className="bg-muted mx-auto mb-40 mt-16 w-full max-w-7xl rounded-xl border p-4">
                <ShopInsuranceNotice />
              </div>
            )}
          </div>
        </FormProvider>
      </ShopInsuranceContext.Provider>

      {!flags.nycLifeDemo && (
        <UnderwritingSlideOut
          open={showUnderwritingSlideOut}
          onClose={() => setShowUnderwritingSlideOut(false)}
          underwritingData={selectedUnderwritingData?.data || null}
          productName={selectedUnderwritingData?.productName || ''}
        />
      )}

      {hasSelectedPolicies && (
        <div className="pointer-events-none fixed bottom-4 left-0 right-0 z-20 flex justify-center md:bottom-6">
          <div className="bg-background pointer-events-auto rounded-full border p-2 shadow-xl shadow-black/5 md:p-3">
            <Button
              size="lg"
              className="rounded-full"
              onClick={() =>
                navigate(`/clients/${client.clientId}/onboarding/policy-application-dashboard`)
              }
            >
              Start Application
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
